import { useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import Button from 'react-bootstrap/Button';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Alert from 'react-bootstrap/Alert';
import { API_BASE } from '../utils/apiBase';

export default function UploadButton({ folder, onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setProgress(0);
    setError('');

    const totalFiles = files.length;
    let completedFiles = 0;

    for (const file of files) {
      try {
        const sigResponse = await fetch(`${API_BASE}/cloudinary-signature`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`
          },
          body: JSON.stringify({
            folder: folder || '',
            tags: folder ? [folder] : []
          })
        });

        const sigData = await sigResponse.json();

        if (!sigResponse.ok) {
          throw new Error(sigData.error || 'Error al obtener firma de Cloudinary');
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sigData.api_key);
        formData.append('timestamp', sigData.timestamp);
        formData.append('signature', sigData.signature);
        if (sigData.folder) {
          formData.append('folder', sigData.folder);
        }
        if (folder) {
          formData.append('tags', folder);
        }

        const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
          {
            method: 'POST',
            body: formData
          }
        );

        const uploadData = await uploadResponse.json();

        if (!uploadResponse.ok) {
          throw new Error(uploadData.error?.message || 'Error al subir imagen');
        }

        completedFiles += 1;
        setProgress(Math.round((completedFiles / totalFiles) * 100));
      } catch (err) {
        setError(`Error subiendo ${file.name}: ${err.message}`);
        console.error('Upload error:', err);
      }
    }

    setUploading(false);
    setProgress(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (completedFiles > 0 && onUploadSuccess) {
      onUploadSuccess();
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/webp"
        multiple
        style={{ display: 'none' }}
      />

      <Button variant="dark" onClick={handleClick} disabled={uploading}>
        {uploading ? `Subiendo... ${progress}%` : 'Subir imagenes'}
      </Button>

      {uploading ? (
        <ProgressBar
          now={progress}
          label={`${progress}%`}
          className="mt-2"
          style={{ width: '200px' }}
        />
      ) : null}

      {error ? (
        <Alert
          variant="danger"
          className="mt-2"
          dismissible
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      ) : null}
    </div>
  );
}
